import type { DiffOp, DiffPayloadV1, PersonResponse, SuggestionStatus } from 'we-tree-api-client/v2';
import i18n from '@/i18n/i18n';
import { getDiffPathLabel as translateDiffPathLabel } from '@/i18n/labelMaps';
import { formatTemporal, parseDateOnly, type DatePrecision } from '@/utils/dateFormatting';

export { canSuggestChangesForMember } from '@/utils/rolePermissions';

/** Fields contributors may propose via DIFF_PAYLOAD v1 (person profile). */
export type PersonSuggestFieldValues = {
  givenName: string;
  middleName: string;
  surname: string;
  suffix: string;
  preferredName: string;
  gender: PersonResponse['gender'];
  birthDate: string;
  birthDatePrecision: string;
  birthPlace: string;
  deathDate: string;
  deathDatePrecision: string;
  deathPlace: string;
  livingStatus: PersonResponse['livingStatus'];
};

const PERSON_DIFF_FIELDS: {
  path: string;
  read: (person: PersonResponse) => unknown;
  writeKey: keyof PersonSuggestFieldValues;
}[] = [
  { path: 'person.givenName', read: (p) => p.givenName, writeKey: 'givenName' },
  { path: 'person.middleName', read: (p) => p.middleName, writeKey: 'middleName' },
  { path: 'person.surname', read: (p) => p.surname, writeKey: 'surname' },
  { path: 'person.suffix', read: (p) => p.suffix, writeKey: 'suffix' },
  { path: 'person.preferredName', read: (p) => p.preferredName, writeKey: 'preferredName' },
  { path: 'person.gender', read: (p) => p.gender, writeKey: 'gender' },
  { path: 'person.birthDate', read: (p) => p.birthDate, writeKey: 'birthDate' },
  { path: 'person.birthDatePrecision', read: (p) => p.birthDatePrecision, writeKey: 'birthDatePrecision' },
  { path: 'person.birthPlace', read: (p) => p.birthPlace, writeKey: 'birthPlace' },
  { path: 'person.deathDate', read: (p) => p.deathDate, writeKey: 'deathDate' },
  { path: 'person.deathDatePrecision', read: (p) => p.deathDatePrecision, writeKey: 'deathDatePrecision' },
  { path: 'person.deathPlace', read: (p) => p.deathPlace, writeKey: 'deathPlace' },
  { path: 'person.livingStatus', read: (p) => p.livingStatus, writeKey: 'livingStatus' }
];

function normalizeDiffValue(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;
  const trimmed = String(value).trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

export function personToSuggestFieldValues(person: PersonResponse): PersonSuggestFieldValues {
  return {
    givenName: person.givenName?.trim() ?? '',
    middleName: person.middleName?.trim() ?? '',
    surname: person.surname?.trim() ?? '',
    suffix: person.suffix?.trim() ?? '',
    preferredName: person.preferredName?.trim() ?? '',
    gender: person.gender,
    birthDate: person.birthDate?.trim() ?? '',
    birthDatePrecision: person.birthDatePrecision ?? 'day',
    birthPlace: person.birthPlace?.trim() ?? '',
    deathDate: person.deathDate?.trim() ?? '',
    deathDatePrecision: person.deathDatePrecision ?? 'day',
    deathPlace: person.deathPlace?.trim() ?? '',
    livingStatus: person.livingStatus
  };
}

export function buildPersonDiffPayload(original: PersonResponse, proposed: PersonSuggestFieldValues): DiffPayloadV1 {
  const ops: DiffOp[] = [];

  // Precision only applies when a date is present — avoid default "day" diffs with no date.
  const effectiveProposed: PersonSuggestFieldValues = {
    ...proposed,
    birthDatePrecision: normalizeDiffValue(proposed.birthDate) ? proposed.birthDatePrecision : '',
    deathDatePrecision: normalizeDiffValue(proposed.deathDate) ? proposed.deathDatePrecision : ''
  };

  for (const field of PERSON_DIFF_FIELDS) {
    let before = normalizeDiffValue(field.read(original));
    if (field.writeKey === 'birthDatePrecision' && !normalizeDiffValue(original.birthDate)) {
      before = undefined;
    }
    if (field.writeKey === 'deathDatePrecision' && !normalizeDiffValue(original.deathDate)) {
      before = undefined;
    }

    const after = normalizeDiffValue(effectiveProposed[field.writeKey]);

    if (before === after) continue;

    if (after === undefined) {
      ops.push({ op: 'unset', path: field.path });
    } else {
      ops.push({ op: 'set', path: field.path, value: after });
    }
  }

  return {
    schemaVersion: 1,
    baseUpdatedAt: original.updatedAt,
    ops
  };
}

/** Draft fact to create via suggestion (client assigns id before submit). */
export type FactSuggestCreate = {
  id: string;
  factTypeId: string;
  factTypeDisplayName: string;
  detail?: string;
  notes?: string;
  sourceNotes?: string;
};

/** Draft changes to an existing fact. */
export type FactSuggestUpdate = {
  personFactId: string;
  factTypeId: string;
  factTypeDisplayName: string;
  detail?: string;
  notes?: string;
  sourceNotes?: string;
  originalDetail?: string;
  originalNotes?: string;
  originalSourceNotes?: string;
  originalFactTypeId?: string;
};

export type LifeEventSuggestCreate = {
  id: string;
  eventTypeId: string;
  eventTypeDisplayName: string;
  eventDate?: string;
  eventDatePrecision?: string;
  eventPlace?: string;
  detail?: string;
  description?: string;
  sourceNotes?: string;
};

export type LifeEventSuggestUpdate = {
  lifeEventId: string;
  eventTypeId: string;
  eventTypeDisplayName: string;
  eventDate?: string;
  eventDatePrecision?: string;
  eventPlace?: string;
  detail?: string;
  description?: string;
  sourceNotes?: string;
  originalEventTypeId?: string;
  originalEventDate?: string;
  originalEventDatePrecision?: string;
  originalEventPlace?: string;
  originalDetail?: string;
  originalDescription?: string;
  originalSourceNotes?: string;
};

/** Draft facts/life-events edits collected in the suggest-changes UI. */
export type SuggestFactsLifeEventsState = {
  factCreates: FactSuggestCreate[];
  factUpdates: FactSuggestUpdate[];
  factDeletes: string[];
  lifeEventCreates: LifeEventSuggestCreate[];
  lifeEventUpdates: LifeEventSuggestUpdate[];
  lifeEventDeletes: string[];
};

export const EMPTY_SUGGEST_FACTS_EVENTS: SuggestFactsLifeEventsState = {
  factCreates: [],
  factUpdates: [],
  factDeletes: [],
  lifeEventCreates: [],
  lifeEventUpdates: [],
  lifeEventDeletes: []
};

type ExtendedDiffOp = { op: string; path?: string; value?: unknown };

function factFieldsEqual(
  a: { factTypeId?: string; detail?: string; notes?: string; sourceNotes?: string },
  b: { factTypeId?: string; detail?: string; notes?: string; sourceNotes?: string }
): boolean {
  return (
    normalizeDiffValue(a.factTypeId) === normalizeDiffValue(b.factTypeId) &&
    normalizeDiffValue(a.detail) === normalizeDiffValue(b.detail) &&
    normalizeDiffValue(a.notes) === normalizeDiffValue(b.notes) &&
    normalizeDiffValue(a.sourceNotes) === normalizeDiffValue(b.sourceNotes)
  );
}

export function buildFactDiffOps(
  creates: FactSuggestCreate[],
  updates: FactSuggestUpdate[],
  deletes: string[]
): ExtendedDiffOp[] {
  const ops: ExtendedDiffOp[] = [];

  for (const create of creates) {
    ops.push({
      op: 'create_person_fact',
      value: {
        id: create.id,
        factTypeId: create.factTypeId,
        factTypeDisplayName: create.factTypeDisplayName,
        detail: normalizeDiffValue(create.detail),
        notes: normalizeDiffValue(create.notes),
        sourceNotes: normalizeDiffValue(create.sourceNotes)
      }
    });
  }

  for (const update of updates) {
    const original = {
      factTypeId: update.originalFactTypeId ?? update.factTypeId,
      detail: update.originalDetail,
      notes: update.originalNotes,
      sourceNotes: update.originalSourceNotes
    };
    const proposed = {
      factTypeId: update.factTypeId,
      detail: update.detail,
      notes: update.notes,
      sourceNotes: update.sourceNotes
    };
    if (factFieldsEqual(original, proposed)) continue;

    ops.push({
      op: 'update_person_fact',
      value: {
        id: update.personFactId,
        factTypeId: update.factTypeId,
        factTypeDisplayName: update.factTypeDisplayName,
        detail: normalizeDiffValue(update.detail),
        notes: normalizeDiffValue(update.notes),
        sourceNotes: normalizeDiffValue(update.sourceNotes)
      }
    });
  }

  for (const personFactId of deletes) {
    ops.push({
      op: 'delete_person_fact',
      value: { id: personFactId }
    });
  }

  return ops;
}

function lifeEventFieldsEqual(
  a: {
    eventTypeId?: string;
    eventDate?: string;
    eventDatePrecision?: string;
    eventPlace?: string;
    detail?: string;
    description?: string;
    sourceNotes?: string;
  },
  b: {
    eventTypeId?: string;
    eventDate?: string;
    eventDatePrecision?: string;
    eventPlace?: string;
    detail?: string;
    description?: string;
    sourceNotes?: string;
  }
): boolean {
  return (
    normalizeDiffValue(a.eventTypeId) === normalizeDiffValue(b.eventTypeId) &&
    normalizeDiffValue(a.eventDate) === normalizeDiffValue(b.eventDate) &&
    normalizeDiffValue(a.eventDatePrecision) === normalizeDiffValue(b.eventDatePrecision) &&
    normalizeDiffValue(a.eventPlace) === normalizeDiffValue(b.eventPlace) &&
    normalizeDiffValue(a.detail) === normalizeDiffValue(b.detail) &&
    normalizeDiffValue(a.description) === normalizeDiffValue(b.description) &&
    normalizeDiffValue(a.sourceNotes) === normalizeDiffValue(b.sourceNotes)
  );
}

export function buildLifeEventDiffOps(
  creates: LifeEventSuggestCreate[],
  updates: LifeEventSuggestUpdate[],
  deletes: string[]
): ExtendedDiffOp[] {
  const ops: ExtendedDiffOp[] = [];

  for (const create of creates) {
    ops.push({
      op: 'create_life_event',
      value: {
        id: create.id,
        eventTypeId: create.eventTypeId,
        eventTypeDisplayName: create.eventTypeDisplayName,
        eventDate: normalizeDiffValue(create.eventDate),
        eventDatePrecision: normalizeDiffValue(create.eventDatePrecision) ?? 'day',
        eventPlace: normalizeDiffValue(create.eventPlace),
        detail: normalizeDiffValue(create.detail),
        description: normalizeDiffValue(create.description),
        sourceNotes: normalizeDiffValue(create.sourceNotes)
      }
    });
  }

  for (const update of updates) {
    const original = {
      eventTypeId: update.originalEventTypeId ?? update.eventTypeId,
      eventDate: update.originalEventDate,
      eventDatePrecision: update.originalEventDatePrecision,
      eventPlace: update.originalEventPlace,
      detail: update.originalDetail,
      description: update.originalDescription,
      sourceNotes: update.originalSourceNotes
    };
    const proposed = {
      eventTypeId: update.eventTypeId,
      eventDate: update.eventDate,
      eventDatePrecision: update.eventDatePrecision,
      eventPlace: update.eventPlace,
      detail: update.detail,
      description: update.description,
      sourceNotes: update.sourceNotes
    };
    if (lifeEventFieldsEqual(original, proposed)) continue;

    ops.push({
      op: 'update_life_event',
      value: {
        id: update.lifeEventId,
        eventTypeId: update.eventTypeId,
        eventTypeDisplayName: update.eventTypeDisplayName,
        eventDate: normalizeDiffValue(update.eventDate),
        eventDatePrecision: normalizeDiffValue(update.eventDatePrecision),
        eventPlace: normalizeDiffValue(update.eventPlace),
        detail: normalizeDiffValue(update.detail),
        description: normalizeDiffValue(update.description),
        sourceNotes: normalizeDiffValue(update.sourceNotes)
      }
    });
  }

  for (const lifeEventId of deletes) {
    ops.push({
      op: 'delete_life_event',
      value: { id: lifeEventId }
    });
  }

  return ops;
}

export function mergeSuggestionDiffPayload(
  personPayload: DiffPayloadV1,
  factOps: ExtendedDiffOp[],
  lifeEventOps: ExtendedDiffOp[]
): DiffPayloadV1 {
  return {
    schemaVersion: 1,
    baseUpdatedAt: personPayload.baseUpdatedAt,
    ops: [...personPayload.ops, ...factOps, ...lifeEventOps] as DiffOp[]
  };
}

export function diffPayloadHasChanges(payload: DiffPayloadV1): boolean {
  return (payload.ops?.length ?? 0) > 0;
}

/** Align with API `assertNoConflict` (compare first 19 chars, T → space). */
export function normalizeSuggestionTimestamp(value: string | null | undefined): string {
  if (!value) return '';
  return String(value).replace('T', ' ').slice(0, 19);
}

export function isSuggestionTargetStale(
  baseUpdatedAt: string | null | undefined,
  currentUpdatedAt: string | null | undefined
): boolean {
  const base = normalizeSuggestionTimestamp(baseUpdatedAt);
  const current = normalizeSuggestionTimestamp(currentUpdatedAt);
  if (!base || !current) return false;
  return base !== current;
}

export function isSuggestionConflictError(error: { status?: number; code?: string } | null | undefined): boolean {
  if (!error) return false;
  return error.status === 409 || error.code === 'CONFLICT';
}

export function getSuggestionStaleTargetComposerMessage(): string {
  return i18n.t('forms:suggest.staleTargetComposer');
}

export function getSuggestionStaleTargetReviewMessage(): string {
  return i18n.t('forms:suggest.staleTargetReview');
}

export function getDiffPathLabel(path: string | undefined, op?: string, value?: unknown): string {
  if (path) return translateDiffPathLabel(path);

  switch (op) {
    case 'create_person_fact':
      return `Add fact: ${(value as { factTypeDisplayName?: string })?.factTypeDisplayName ?? 'Fact'}`;
    case 'update_person_fact':
      return `Update fact: ${(value as { factTypeDisplayName?: string })?.factTypeDisplayName ?? 'Fact'}`;
    case 'delete_person_fact':
      return 'Remove fact';
    case 'create_life_event':
      return `Add life event: ${(value as { eventTypeDisplayName?: string })?.eventTypeDisplayName ?? 'Event'}`;
    case 'update_life_event':
      return `Update life event: ${(value as { eventTypeDisplayName?: string })?.eventTypeDisplayName ?? 'Event'}`;
    case 'delete_life_event':
      return 'Remove life event';
    case 'create_relationship':
      return 'Add relationship';
    case 'update_relationship':
      return 'Update relationship';
    case 'delete_relationship':
      return 'Remove relationship';
    default:
      return 'Change';
  }
}

export function formatDiffOpAction(op: string): string {
  if (op === 'unset' || op.startsWith('delete_')) return 'Remove';
  if (op.startsWith('create_')) return 'Add';
  if (op.startsWith('update_')) return 'Update';
  return 'Set to';
}

const PERSON_DATE_PATHS = new Set(['person.birthDate', 'person.deathDate']);

function asDatePrecision(value: unknown): DatePrecision | undefined {
  return value === 'year' || value === 'month' || value === 'day' ? value : undefined;
}

function precisionForPersonDatePath(
  path: string,
  ops?: ReadonlyArray<Pick<DiffOp, 'path' | 'op' | 'value'>>
): DatePrecision {
  const precisionPath = path === 'person.birthDate' ? 'person.birthDatePrecision' : 'person.deathDatePrecision';
  const precisionOp = ops?.find((op) => op.path === precisionPath && op.op === 'set');
  return asDatePrecision(precisionOp?.value) ?? 'day';
}

function formatStoredDate(value: string, precision: DatePrecision = 'day'): string | null {
  if (!parseDateOnly(value)) return null;
  const formatted = formatTemporal(value, precision);
  return formatted === 'N/A' ? null : formatted;
}

export function formatDiffOpValue(
  value: unknown,
  path?: string,
  ops?: ReadonlyArray<Pick<DiffOp, 'path' | 'op' | 'value'>>
): string {
  if (value === null || value === undefined || value === '') return '—';

  if (typeof value === 'string') {
    if (path && PERSON_DATE_PATHS.has(path)) {
      return formatStoredDate(value, precisionForPersonDatePath(path, ops)) ?? value;
    }
    return value;
  }

  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const parts: string[] = [];

    if (typeof obj.detail === 'string' && obj.detail.trim()) {
      parts.push(obj.detail.trim());
    }
    if (typeof obj.eventPlace === 'string' && obj.eventPlace.trim()) {
      parts.push(obj.eventPlace.trim());
    }
    if (typeof obj.eventDate === 'string' && obj.eventDate.trim()) {
      const precision = asDatePrecision(obj.eventDatePrecision) ?? 'day';
      parts.push(formatStoredDate(obj.eventDate, precision) ?? obj.eventDate.trim());
    }
    if (typeof obj.description === 'string' && obj.description.trim()) {
      parts.push(obj.description.trim());
    }
    if (typeof obj.notes === 'string' && obj.notes.trim()) {
      parts.push(obj.notes.trim());
    }
    if (typeof obj.sourceNotes === 'string' && obj.sourceNotes.trim()) {
      parts.push(obj.sourceNotes.trim());
    }

    if (parts.length > 0) return parts.join(' · ');

    // Structured create/update ops without user-facing fields (e.g. delete payloads)
    if (
      'factTypeId' in obj ||
      'factTypeDisplayName' in obj ||
      'eventTypeId' in obj ||
      'eventTypeDisplayName' in obj ||
      'id' in obj
    ) {
      return '—';
    }

    return JSON.stringify(value);
  }

  return String(value);
}

export function canAccessReviewQueue(familyRole: string | null | undefined): boolean {
  return familyRole === 'owner' || familyRole === 'admin' || familyRole === 'editor';
}

export function suggestionStatusLabel(status: SuggestionStatus | string): string {
  switch (status) {
    case 'pending':
      return i18n.t('labels:suggestionStatus.pending');
    case 'approved':
      return i18n.t('labels:suggestionStatus.approved');
    case 'rejected':
      return i18n.t('labels:suggestionStatus.rejected');
    case 'changes_requested':
      return i18n.t('labels:suggestionStatus.changes_requested');
    case 'cancelled':
      return i18n.t('labels:suggestionStatus.cancelled');
    default:
      return status;
  }
}

/** Pending evidence item collected in the suggest composer before attach. */
export type SuggestionEvidenceDraft = {
  /** Existing source id, or omit when creating a website source. */
  sourceId?: string;
  /** New website source fields when sourceId is absent. */
  newSource?: {
    title: string;
    url: string;
  };
  /** Existing citation id on the chosen source, or omit when creating. */
  citationId?: string;
  /** New citation fields when citationId is absent. */
  newCitation?: {
    detailLocator?: string;
    notes?: string;
    mediaAssetId?: string;
  };
  /** Diff path this citation supports (optional). */
  targetPath?: string;
  supports?: boolean;
};

export type CreatePersonSuggestionPayload = {
  familyMemberId: string;
  personId: string;
  title?: string;
  description?: string;
  fieldValues: PersonSuggestFieldValues;
  originalPerson: PersonResponse;
  factCreates?: FactSuggestCreate[];
  factUpdates?: FactSuggestUpdate[];
  factDeletes?: string[];
  lifeEventCreates?: LifeEventSuggestCreate[];
  lifeEventUpdates?: LifeEventSuggestUpdate[];
  lifeEventDeletes?: string[];
  /** Optional evidence to attach after create. */
  evidence?: SuggestionEvidenceDraft[];
};

export type CreatePersonSuggestionResult = {
  success: boolean;
  data?: import('we-tree-api-client/v2').SuggestionResponse;
  error?: Error;
  /** Suggestion was created but citation attach failed. */
  evidenceFailed?: boolean;
  evidenceErrorMessage?: string;
};

/** Collect unique changed paths from a diff for evidence target-path picker. */
export function collectDiffTargetPaths(payload: DiffPayloadV1): string[] {
  const paths: string[] = [];
  const seen = new Set<string>();
  for (const op of payload.ops ?? []) {
    if (!op.path || seen.has(op.path)) continue;
    seen.add(op.path);
    paths.push(op.path);
  }
  return paths;
}
